import { useEffect, useMemo, useRef, useState } from 'react';

import { getServiceId } from '@/api/tokens.helper';
import type { HolidaySuggestion, ProactiveSuggestion } from '@/store/proactive/proactive.mappers';
import {
  type BlankHolidayPlan,
  useProactiveSuggestionsQuery,
  useAcceptSuggestionMutation,
  useDismissSuggestionMutation,
  useHolidaysByYears,
  useHolidaySuggestionUi,
  useIsHolidayPlanner,
  useMarkSuggestionSeenMutation,
  useRequestHolidayIdeaMutation,
  useSnoozeSuggestionMutation,
} from '@/store/proactive/proactive.store';
import { toast } from '@/utils/toast';

import type { WizardSeed } from '../EventCard/EventWizardModal/eventWizard.types';
import { EventWizardModal } from '../EventCard/EventWizardModal/EventWizardModal';

import { HabitSuggestionModal } from './HabitSuggestionModal';
import { HolidaySuggestionModal } from './HolidaySuggestionModal';
import { HolidayTodayModal } from './HolidayTodayModal';

// How many regulars from past events to invite by default — the owner trims
// the list on the Audience step.
const REGULAR_GUESTS = 6;

/**
 * The idea as a filled-in event: time, place, type. A holiday also brings the
 * usual guests and starts the AI plan and cover straight away, so by the time
 * the owner reaches those steps they are waiting. A habit is the routine's
 * usual time and place only: its regulars are named on the card and picked on
 * the Audience step, never invited on their own. The crew joins on its own.
 * Everything stays editable.
 */
const seedFrom = (suggestion: ProactiveSuggestion): WizardSeed => {
  const prefill: WizardSeed = {
    title: suggestion.eventPrefill.title,
    description: suggestion.eventPrefill.description,
    startDate: suggestion.eventPrefill.start,
    endDate: suggestion.eventPrefill.end,
    locations: suggestion.eventPrefill.locations,
    eventType: suggestion.eventPrefill.eventType ?? undefined,
  };
  if (suggestion.kind === 'habit') return prefill;
  return { ...prefill, pastGuests: REGULAR_GUESTS, autoPlan: true, autoCover: true };
};

// A plain holiday plan has only a day: a daytime window the owner moves freely.
const atHour = (day: Date, hour: number) =>
  new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour);

const blankSeed = (plan: BlankHolidayPlan): WizardSeed => ({
  title: plan.title,
  startDate: atHour(plan.date, 10),
  endDate: atHour(plan.date, 18),
});

const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

// "Today is <holiday>" shows once a day per vessel on this device — a per-viewer
// convenience, so browser storage is enough; failing storage just shows it again.
const todayShownKey = (vesselId: string, day: Date) =>
  `vh:holiday-today:${vesselId}:${day.toDateString()}`;
const wasShownToday = (key: string) => {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};
const markShownToday = (key: string) => {
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    // storage unavailable (private mode) — at worst the popup shows again
  }
};

/**
 * Mounted once in the app shell. Two ways into the same card:
 *
 * - **arrival** — a new open suggestion nobody has seen yet pops up on its own,
 *   once: it is marked seen on the server the moment it shows, so it does not
 *   come back on the next load or on another device;
 * - **calendar** — the glowing badge (`HolidayLabel` with a suggestion, or the
 *   `HabitLabel` of a routine) opens it on demand, as often as wanted, while it
 *   is open or snoozed.
 *
 * Holiday ideas and habits share it; a habit card also has "Not this time".
 *
 * "Plan it" hands the idea to the event wizard and accepts the suggestion only
 * once the event exists; cancelling the wizard leaves the suggestion as it was.
 * A holiday without an idea (any day picked in the calendar, or "Plan an event"
 * on the holiday itself) opens the same wizard on that day with its name.
 * On the holiday day itself, "Today is <holiday>" shows once.
 */
export const HolidaySuggestionMount = () => {
  const { data: suggestions } = useProactiveSuggestionsQuery();
  const {
    openId,
    openSnapshot,
    planningId,
    blankPlan,
    open,
    close,
    startPlanning,
    planBlank,
    stopPlanning,
  } = useHolidaySuggestionUi();
  const isPlanner = useIsHolidayPlanner();

  const { mutate: markSeen } = useMarkSuggestionSeenMutation();
  const { mutate: accept } = useAcceptSuggestionMutation();
  const snooze = useSnoozeSuggestionMutation();
  const dismiss = useDismissSuggestionMutation();
  const requestIdea = useRequestHolidayIdeaMutation();

  const byId = useMemo(() => new Map((suggestions ?? []).map((s) => [s.id, s])), [suggestions]);
  const current = openId
    ? (byId.get(openId) ?? (openSnapshot?.id === openId ? openSnapshot : null))
    : null;
  // Kept aside while planning: the wizard outlives the card, and its onCreated
  // fires after it has closed itself.
  const [planned, setPlanned] = useState<ProactiveSuggestion | null>(null);

  // Arrival: show the oldest unseen open suggestion, one at a time.
  const shownRef = useRef(new Set<string>());
  const hasUnseen = (suggestions ?? []).some((s) => s.status === 'open' && !s.seenAt);
  useEffect(() => {
    if (openId || planningId || blankPlan || !suggestions) return;
    const next = suggestions
      .filter((s) => s.status === 'open' && !s.seenAt && !shownRef.current.has(s.id))
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())[0];
    if (!next) return;
    shownRef.current.add(next.id);
    open(next.id);
    markSeen({ id: next.id });
  }, [suggestions, openId, planningId, blankPlan, open, markSeen]);

  // "Today is <holiday>": once a day, only when nothing else is on screen.
  const [today] = useState(() => new Date());
  const holidays = useHolidaysByYears([today.getFullYear()]);
  const todayHoliday = holidays.find((h) => sameDay(h.date, today));
  const todayIdea = todayHoliday
    ? (suggestions ?? []).find(
        (s): s is HolidaySuggestion =>
          s.kind === 'holiday' &&
          s.holiday.key === todayHoliday.key &&
          sameDay(s.holiday.date, todayHoliday.date),
      )
    : undefined;
  const vesselId = getServiceId();
  const todayKey = vesselId ? todayShownKey(vesselId, today) : null;
  // Read once per session: was it already shown today on this device?
  const [shownBefore] = useState(() => (todayKey ? wasShownToday(todayKey) : true));
  const [todayClosed, setTodayClosed] = useState(false);
  const todayOpen =
    isPlanner &&
    !!todayHoliday &&
    !!suggestions &&
    !shownBefore &&
    !todayClosed &&
    !openId &&
    !planningId &&
    !blankPlan &&
    !hasUnseen;
  useEffect(() => {
    if (todayOpen && todayKey) markShownToday(todayKey);
  }, [todayOpen, todayKey]);

  const busy = snooze.isPending || dismiss.isPending;

  const handlePlan = () => {
    if (!current) return;
    setPlanned(current);
    startPlanning(current.id);
  };

  const handleRemindLater = (until: Date) => {
    if (!current) return;
    const when = until.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
    snooze.mutate(
      { id: current.id, until },
      { onSuccess: () => toast.success(`Your reminder has been set for ${when}.`) },
    );
    close();
  };

  const handleMute = (scope: 'topic' | 'kind') => {
    if (!current) return;
    const message =
      current.kind === 'holiday'
        ? scope === 'topic'
          ? `No more ideas for ${current.holiday.name}`
          : 'Holiday ideas turned off'
        : scope === 'topic'
          ? 'This suggestion has been turned off.'
          : 'Habit suggestions turned off';
    dismiss.mutate(
      { id: current.id, muteTopic: scope === 'topic', muteKind: scope === 'kind' },
      { onSuccess: () => toast.success(message) },
    );
    close();
  };

  // A habit only: skip this round, the routine is offered again next time.
  const handleSkip = () => {
    if (!current) return;
    dismiss.mutate(
      { id: current.id },
      { onSuccess: () => toast.success('Your habit has been skipped this time.') },
    );
    close();
  };

  const handleCreated = (eventId: string) => {
    if (planned && !blankPlan) accept({ id: planned.id, eventId });
  };

  const wizardSeed = blankPlan ? blankSeed(blankPlan) : planned ? seedFrom(planned) : undefined;

  return (
    <>
      <HolidaySuggestionModal
        suggestion={current?.kind === 'holiday' ? current : null}
        onClose={close}
        onPlan={handlePlan}
        onRemindLater={handleRemindLater}
        onMute={handleMute}
        busy={busy}
      />
      <HabitSuggestionModal
        suggestion={current?.kind === 'habit' ? current : null}
        onClose={close}
        onSkip={handleSkip}
        onPlan={handlePlan}
        onRemindLater={handleRemindLater}
        onMute={handleMute}
        busy={busy}
      />
      {todayOpen && todayHoliday && (
        <HolidayTodayModal
          holiday={todayHoliday}
          suggestion={todayIdea}
          busy={requestIdea.isPending}
          onClose={() => setTodayClosed(true)}
          onOpenIdea={() => {
            setTodayClosed(true);
            if (todayIdea) open(todayIdea.id);
          }}
          onGetIdea={() =>
            requestIdea.mutate(
              { key: todayHoliday.key, year: todayHoliday.date.getFullYear() },
              {
                onSuccess: (idea) => {
                  setTodayClosed(true);
                  open(idea.id, idea);
                },
              },
            )
          }
          onPlan={() => {
            setTodayClosed(true);
            planBlank({ title: todayHoliday.name, date: todayHoliday.date });
          }}
        />
      )}
      <EventWizardModal
        open={!!blankPlan || (!!planningId && !!planned)}
        onClose={stopPlanning}
        seed={wizardSeed}
        onCreated={handleCreated}
      />
    </>
  );
};

export default HolidaySuggestionMount;
